import { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import isGHMC, { tableRowBg } from "../../../Common/helper/helpar";

import {
  PAGINATION_COLORS,
  paginateRows,
} from "./reportCommon";

import { DEFAULT_PRIMARY_VEHICLE_FILTERS } from "./staticReportData";
import { GetCircle } from "../../../Apis/organizationApi";

import PrimaryVehicleDeployFilter from "./PrimaryVehicleDeployFilter";
import DelhiReportExport from "./DelhiReportExport";

import {
  GetPrimaryVehicleDeploymentReport,
} from "./ReportApi";


const HEADER_STYLE = {
  textAlign: "center",
  border: "1px solid #ddd",
  padding: "10px",
  color: "#fff",
  fontWeight: 600,
  fontSize: "12px",
  whiteSpace: "normal",
};

const CELL_STYLE = {
  textAlign: "center",
  border: "1px solid #ddd",
  padding: "10px",
  fontSize: "13px",
  whiteSpace: "normal",
};


const exportColumns = [
  {
    label: "Ward",
    field: "wardName",
  },
  {
    label: "Planned Vehicles",
    field: "plannedVehicles",
  },
  {
    label: "Vehicles Deployed (UHF Reader)",
    field: "vehiclesDeployed",
  },
  {
    label: "Vehicle Reached To First Pickup Point (GPS)",
    field: "reachedFirstPickup",
  },
  {
    label: "% Of Deployment",
    render: (row) =>
      `${row.percentageOfDeployment}%`,
  },
];


/*
 * Convert date to yyyy-MM-dd
 */
const formatDateForApi = (date) => {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  if (typeof date === "string") {
    return date.substring(0, 10);
  }

  return null;
};


/*
 * Get WardId from filter.
 */
const getWardId = (values) => {
  if (!values) {
    return null;
  }

  return (
    values.wardId ??
    values.WardId ??
    values.wardID ??
    values.ward ??
    null
  );
};


/*
 * Convert API response into table format.
 *
 * API:
 *
 * {
 *   success: true,
 *   data: [
 *     {
 *       wardId: 1,
 *       ward: "Ward 1",
 *       plannedVehicles: 10,
 *       vehiclesDeployed: 8,
 *       vehicleReachedToFirstPickupPoint: 7,
 *       percentageOfDeployment: 70
 *     }
 *   ]
 * }
 */
const getResponseData = (apiResponse) => {
  if (Array.isArray(apiResponse)) {
    return apiResponse;
  }

  return (
    apiResponse?.data ??
    apiResponse?.Data ??
    apiResponse?.result ??
    apiResponse?.Result ??
    []
  );
};

const normalizeReportData = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map((item) => ({
      wardId:
        item.wardId ??
        item.WardId ??
        null,

      wardName:
        item.ward ??
        item.wardName ??
        item.Ward ??
        item.WardName ??
        "",

      plannedVehicles:
        Number(
          item.plannedVehicles ??
          item.plannedVehicle ??
          item.PlannedVehicles ??
          item.PlannedVehicle ??
          0
        ),

      vehiclesDeployed:
        Number(
          item.vehiclesDeployed ??
          item.vehicleDeployed ??
          item.vehiclesDeployedByUhfReader ??
          item.VehiclesDeployed ??
          item.VehicleDeployed ??
          item.VehiclesDeployedByUhfReader ??
          0
        ),

      reachedFirstPickup:
        Number(
          item.vehicleReachedToFirstPickupPoint ??
          item.VehicleReachedToFirstPickupPoint ??
          item.vehicleReachedToFirstPickupPointGps ??
          item.VehicleReachedToFirstPickupPointGps ??
          item.reachedFirstPickup ??
          0
        ),

      percentageOfDeployment:
        Number(
          item.percentageOfDeployment ??
          item.PercentageOfDeployment ??
          item.deploymentPercentage ??
          item.DeploymentPercentage ??
          0
        ),
    }))
    .filter(
      (item) =>
        item.wardName
    );
};


const PrimaryVehicleDeployReport = () => {

  const isProd = isGHMC();

  const headerBg = isProd
    ? "#004554"
    : "#003366";


  /*
   * Filter state
   */
  const [wardOptions, setWardOptions] = useState([]);

  const [filters, setFilters] =
    useState(
      DEFAULT_PRIMARY_VEHICLE_FILTERS
    );

  const [
    appliedFilters,
    setAppliedFilters,
  ] = useState(
    DEFAULT_PRIMARY_VEHICLE_FILTERS
  );


  /*
   * THIS contains API DATA.
   *
   * No static PRIMARY_VEHICLE_DEPLOY_DATA.
   */
  const [
    reportData,
    setReportData,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  /*
   * Pagination
   */
  const [
    page,
    setPage,
  ] = useState(0);

  const [
    rowsPerPage,
    setRowsPerPage,
  ] = useState(10);


  /*
   * ============================================
   * API CALL
   * ============================================
   *
   * API:
   *
   * GET
   * /api/v1/MasterDetails/PrimaryVehicleDeploymentReport
   *
   * Parameters:
   *
   * WardId
   * fromDate
   * toDate
   */
  useEffect(() => {
    const loadWards = async () => {
      try {
        const response = await GetCircle();
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setWardOptions(list.filter((ward) => ward.isActive !== false));
      } catch (err) {
        console.error("Failed to load primary vehicle deploy wards:", err);
        setWardOptions([]);
      }
    };

    loadWards();
  }, []);

  useEffect(() => {

    let isMounted = true;

    const fetchReport = async () => {

      try {

        setLoading(true);

        setError("");


        /*
         * Get values from filter
         */
        const wardId =
          getWardId(
            appliedFilters
          );


        const fromDate =
          formatDateForApi(
            appliedFilters?.fromDate ??
            appliedFilters?.FromDate ??
            appliedFilters?.fromDateTime ??
            appliedFilters?.FromDateTime ??
            appliedFilters?.startDate
          );


        const toDate =
          formatDateForApi(
            appliedFilters?.toDate ??
            appliedFilters?.ToDate ??
            appliedFilters?.toDateTime ??
            appliedFilters?.ToDateTime ??
            appliedFilters?.endDate
          );


        /*
         * API payload
         */
        const payload = {
          wardId,
          fromDate,
          toDate,
        };


        console.log(
          "Primary Vehicle Report Payload:",
          payload
        );


        /*
         * CALL API SERVICE
         */
        const response =
          await GetPrimaryVehicleDeploymentReport(
            payload
          );


        console.log(
          "Primary Vehicle Report API Response:",
          response
        );


        if (!isMounted) {
          return;
        }


        /*
         * Axios response:
         *
         * response.data
         *
         * {
         *   success: true,
         *   data: [...]
         * }
         */


        const apiResponse =
          response?.data;


        if (
          !apiResponse
        ) {
          throw new Error(
            "Empty response received from API."
          );
        }


        if (apiResponse.success === false) {
          throw new Error(
            apiResponse.message ||
            "Unable to fetch report."
          );
        }


        /*
         * Convert API data
         */
        const rows =
          normalizeReportData(
            getResponseData(apiResponse)
          );


        console.log(
          "Normalized Primary Vehicle Data:",
          rows
        );


        /*
         * UPDATE TABLE DATA
         */
        setReportData(
          rows
        );


        /*
         * Go to first page
         */
        setPage(0);

      } catch (err) {

        if (!isMounted) {
          return;
        }


        console.error(
          "Primary Vehicle Deployment Report Error:",
          err
        );


        setReportData([]);


        setError(
          err?.response?.data?.message ||
          err?.message ||
          "Unable to fetch primary vehicle deployment report."
        );

      } finally {

        if (isMounted) {
          setLoading(false);
        }

      }
    };


    fetchReport();


    return () => {
      isMounted = false;
    };

  }, [appliedFilters]);


  /*
   * ============================================
   * TABLE DATA
   * ============================================
   *
   * API has already applied:
   *
   * WardId
   * FromDate
   * ToDate
   *
   * Therefore don't filter again here.
   */
  const filteredRows = useMemo(
    () => reportData,
    [reportData]
  );


  /*
   * Pagination
   */
  const paginatedRows =
    useMemo(
      () =>
        paginateRows(
          filteredRows,
          page,
          rowsPerPage
        ),
      [
        filteredRows,
        page,
        rowsPerPage,
      ]
    );


  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredRows.length /
        rowsPerPage
      )
    );


  const rangeStart =
    filteredRows.length > 0
      ? page * rowsPerPage + 1
      : 0;


  const rangeEnd =
    Math.min(
      (page + 1) *
        rowsPerPage,
      filteredRows.length
    );


  /*
   * ============================================
   * SEARCH
   * ============================================
   */
  const handleSearch = (
    values
  ) => {

    console.log(
      "Selected Filters:",
      values
    );


    setFilters(
      values
    );


    /*
     * This triggers useEffect
     * and calls API.
     */
    setAppliedFilters(
      values
    );


    setPage(0);
  };


  /*
   * ============================================
   * RESET
   * ============================================
   */
  const handleReset = () => {

    setFilters(
      DEFAULT_PRIMARY_VEHICLE_FILTERS
    );


    setAppliedFilters(
      DEFAULT_PRIMARY_VEHICLE_FILTERS
    );


    setReportData([]);


    setError("");


    setPage(0);
  };


  /*
   * Rows per page
   */
  const handleRowsPerPageChange =
    (event) => {

      setRowsPerPage(
        Number(
          event.target.value
        )
      );

      setPage(0);
    };


  return (
    <div className="content text-start">

      <div className="page-content">

        {/* =====================================
            HEADER
        ====================================== */}

        <div
          className="
            d-flex
            flex-column
            flex-md-row
            justify-content-between
            align-items-start
            gap-3
            mt-3
            mb-3
          "
        >

          <div className="me-md-3">

            <h5 className="mb-1 fw-semibold">
              Primary Vehicle Deployment
            </h5>

           

          </div>


          <DelhiReportExport
            reportData={
              filteredRows
            }
            reportTitle="Primary Vehicle Deploy"
            columns={
              exportColumns
            }
            fileName="PrimaryVehicleDeployReport"
          />

        </div>


        {/* =====================================
            FILTER
        ====================================== */}

        <div className="card shadow mb-3">

          <div className="card-body">

            <PrimaryVehicleDeployFilter
              initialValues={
                filters
              }
              wardOptions={
                wardOptions
              }
              onSearch={
                handleSearch
              }
              onReset={
                handleReset
              }
            />

          </div>

        </div>


        {/* =====================================
            REPORT
        ====================================== */}

        <div className="card shadow">

          <div className="card-body">



            <Paper>

              <TableContainer
                sx={{
                  width: "100%",
                  maxHeight: 500,
                  border:
                    "1px solid #ccc",
                }}
              >

                <Table stickyHeader>

                  {/* TABLE HEADER */}

                  <TableHead>

                    <TableRow>

                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        S.No
                      </TableCell>


                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        Ward
                      </TableCell>


                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        Planned Vehicles
                      </TableCell>


                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        Vehicles Deployed
                        <br />
                        (UHF Reader)
                      </TableCell>


                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        Vehicle Reached To
                        First Pickup Point
                        <br />
                        (GPS)
                      </TableCell>


                      <TableCell
                        sx={{
                          ...HEADER_STYLE,
                          backgroundColor:
                            headerBg,
                        }}
                      >
                        % Of Deployment
                      </TableCell>

                    </TableRow>

                  </TableHead>


                  {/* TABLE BODY */}

                  <TableBody>

                    {/* LOADING */}

                    {loading && (

                      <TableRow>

                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={CELL_STYLE}
                        >
                          Loading...
                        </TableCell>

                      </TableRow>

                    )}


                    {/* ERROR */}

                    {!loading &&
                      error && (

                        <TableRow>

                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{
                              ...CELL_STYLE,
                              color:
                                "#dc2626",
                            }}
                          >
                            {error}
                          </TableCell>

                        </TableRow>

                      )}


                    {/* NO DATA */}

                    {!loading &&
                      !error &&
                      paginatedRows.length ===
                        0 && (

                        <TableRow>

                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={CELL_STYLE}
                          >
                            No data available
                          </TableCell>

                        </TableRow>

                      )}


                    {/* API DATA */}

                    {!loading &&
                      !error &&
                      paginatedRows.length >
                        0 &&
                      paginatedRows.map(
                        (
                          row,
                          rowIndex
                        ) => (

                          <TableRow
                            key={
                              row.wardId ??
                              `${row.wardName}-${rowIndex}`
                            }
                            sx={{
                              backgroundColor:
                                tableRowBg(
                                  rowIndex
                                ),
                            }}
                          >

                            {/* S.NO */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                page *
                                  rowsPerPage +
                                  rowIndex +
                                  1
                              }
                            </TableCell>


                            {/* WARD */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                row.wardName
                              }
                            </TableCell>


                            {/* PLANNED */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                row.plannedVehicles
                              }
                            </TableCell>


                            {/* UHF */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                row.vehiclesDeployed
                              }
                            </TableCell>


                            {/* GPS */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                row.reachedFirstPickup
                              }
                            </TableCell>


                            {/* PERCENTAGE */}

                            <TableCell
                              sx={
                                CELL_STYLE
                              }
                            >
                              {
                                row.percentageOfDeployment
                              }%
                            </TableCell>

                          </TableRow>

                        )
                      )}

                  </TableBody>

                </Table>

              </TableContainer>


              {/* =================================
                  PAGINATION
              ================================== */}

              <div
                style={{
                  marginTop: 16,
                  padding:
                    "12px 16px",
                  display: "flex",
                  flexWrap:
                    "wrap",
                  alignItems:
                    "center",
                  justifyContent:
                    "space-between",
                  gap: 16,
                }}
              >

                <p
                  style={{
                    margin: 0,
                    fontSize:
                      "14px",
                    color:
                      PAGINATION_COLORS.textMuted,
                  }}
                >
                  Showing{" "}
                  {rangeStart}-
                  {rangeEnd} of{" "}
                  {
                    filteredRows.length
                  }{" "}
                  results
                </p>


                <div
                  style={{
                    display:
                      "flex",
                    flexWrap:
                      "wrap",
                    alignItems:
                      "center",
                    gap: 20,
                  }}
                >

                  {/* ROWS PER PAGE */}

                  <label
                    style={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      gap: 8,
                      fontSize:
                        "14px",
                      color:
                        PAGINATION_COLORS.textMuted,
                      margin: 0,
                    }}
                  >

                    Row per page

                    <select
                      value={
                        rowsPerPage
                      }
                      onChange={
                        handleRowsPerPageChange
                      }
                      style={{
                        appearance:
                          "none",
                        borderRadius: 6,
                        border: `1px solid ${PAGINATION_COLORS.border}`,
                        backgroundColor:
                          "#F0F4F8",
                        padding:
                          "6px 28px 6px 12px",
                        fontSize:
                          "14px",
                        color:
                          "#374151",
                        outline:
                          "none",
                      }}
                    >

                      {[10, 25, 50, 100].map(
                        (size) => (

                          <option
                            key={
                              size
                            }
                            value={
                              size
                            }
                          >
                            {size}
                          </option>

                        )
                      )}

                    </select>

                  </label>


                  {/* PAGINATION */}

                  <nav
                    style={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      gap: 6,
                    }}
                    aria-label="Pagination"
                  >

                    {/* PREVIOUS */}

                    <button
                      type="button"
                      onClick={() =>
                        setPage(
                          Math.max(
                            0,
                            page - 1
                          )
                        )
                      }
                      disabled={
                        page === 0
                      }
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        width: 32,
                        height: 32,
                        border:
                          "none",
                        background:
                          "transparent",
                        color:
                          "#9CA3AF",
                        cursor:
                          page ===
                          0
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          page ===
                          0
                            ? 0.4
                            : 1,
                      }}
                      aria-label="Previous page"
                    >
                      <FiChevronLeft
                        size={16}
                      />
                    </button>


                    {/* PAGE NUMBERS */}

                    {Array.from(
                      {
                        length:
                          totalPages,
                      },
                      (
                        _,
                        index
                      ) =>
                        index + 1
                    )
                      .slice(0, 6)
                      .map(
                        (
                          pageNumber
                        ) => (

                          <button
                            key={
                              pageNumber
                            }
                            type="button"
                            onClick={() =>
                              setPage(
                                pageNumber -
                                  1
                              )
                            }
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "center",
                              minWidth:
                                32,
                              height:
                                32,
                              borderRadius:
                                6,
                              padding:
                                "0 10px",
                              border:
                                "none",
                              fontSize:
                                "14px",
                              fontWeight:
                                500,
                              cursor:
                                "pointer",
                              backgroundColor:
                                page +
                                  1 ===
                                pageNumber
                                  ? PAGINATION_COLORS.navy
                                  : "#F0F4F8",
                              color:
                                page +
                                  1 ===
                                pageNumber
                                  ? "#FFFFFF"
                                  : "#374151",
                            }}
                            aria-current={
                              page +
                                1 ===
                              pageNumber
                                ? "page"
                                : undefined
                            }
                          >
                            {
                              pageNumber
                            }
                          </button>

                        )
                      )}


                    {/* NEXT */}

                    <button
                      type="button"
                      onClick={() =>
                        setPage(
                          Math.min(
                            totalPages -
                              1,
                            page + 1
                          )
                        )
                      }
                      disabled={
                        page + 1 ===
                        totalPages
                      }
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        width: 32,
                        height: 32,
                        border:
                          "none",
                        background:
                          "transparent",
                        color:
                          "#9CA3AF",
                        cursor:
                          page + 1 ===
                          totalPages
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          page + 1 ===
                          totalPages
                            ? 0.4
                            : 1,
                      }}
                      aria-label="Next page"
                    >
                      <FiChevronRight
                        size={16}
                      />
                    </button>

                  </nav>

                </div>

              </div>

            </Paper>

          </div>

        </div>

      </div>
    </div>
  );
};

export default PrimaryVehicleDeployReport;